import React, { useState, useRef, useEffect } from "react";
import { useForm } from "react-hook-form";
import { themes } from "services/constants";
import { ShiftWorkTable } from "../Component/ShiftWorkTable";
import {
  sftType,
  otPreauthorizationOptions,
  otTimingOptions,
  calculationOptions,
  otPaymentOptions,
  weekoffPaymentOptions,
  holidayPaymentOptions,
  ShiftWorkExample,
  PaymentBasedAttendanceExample,
  PaymentBasedFixedAmountExample,
  PaidWeekOffExample,
  PaidHolidayExample,
  boxStyle,
  isAbsenceOptions,
  IsAbsenceExample,
  IsAbsencePaidExample,
  CustomBox,
  optionsStyle,
  individualBoxStyle,
  amtFactorStyle,
  infoMessageStyle,
  additionalInfoStyle,
  cardStyle,
  mainOptions,
} from "../Component/ShiftCategoryOptions";
import { CustomButton } from "components/CustomComponents";
import { CustomAutoComplete } from "components/CustomComponents";

import { Box, Switch, Typography, RadioGroup, Radio } from "@mui/material";

export const Earning = ({
  onUpdate,
  handleNext,
  handleBack,
  data,
  clearAbsenceValues,
  setClearAbsenceValues,
}) => {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    resetField,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      is_overtime_allowed: false,
      ot_work_preauthorization: "Automatic",
      ot_timing_privilege: "Only Before",
      calculation_based_on: "Hours",
      ot_sft_payment_factor: 0,
      ot_attendance_payment_factor: 1,
      minutes_for_consideration: 0,
      additional_minutes_for_consideration: 0,
      deduct_additional_percentage: 0,
      deduct_shift_count: 0,
      minimum_ot_consideration: 0,
      additional_ot: 0,
      ot_fixed_amount_thereshold_limit: 0,
      ot_fixed_amount_payable: 0,
      ot_fixed_amount_additional_mins: 0,
      ot_fixed_amount_additional_payable: 0,
      is_ot_pay_by_default: false,
      is_ot_payment_based_on: "Attendance",
      is_weekoff_payment_based_on: "Attendance",
      is_paid_weekoff: false,
      is_payment_for_weekoff_day_attendance: false,
      is_holiday_payment_based_on: "Attendance",
      is_paid_holiday: false,
      is_payment_for_holiday_day_attendance: false,
      holiday_attendance_payment_factor: 0,
      holiday_attendance_fixed_amount: 0,
      weekoff_attendance_payment_factor: 0,
      weekoff_attendance_fixed_amount: 0,
      attendance_deduct_type: "",
      fixed_amount_deduct_type: "",
      is_weekoff_paid: false,
      is_holiday_paid: false,
      is_absence_paid: false,
      absent_payment_based_on: "Paid",
      partial_payment_factor: 1,
    },
  });
  const [rows, setRows] = useState([
    { work_min_percentage: "", count_as_shift: "" },
  ]);
  const [rowError, setRowError] = useState([
    { work_min_percentage: false, count_as_shift: false },
  ]);
  const validateTableRef = useRef(null);
  const handleOTPreauthorization = (event) => {
    const value = event.target.value;
    setValue("ot_work_preauthorization", value);
    setValue("ot_timing_privilege", "Only Before");
  };

  const hanldeOTPrivilege = (event) => {
    setValue("ot_timing_privilege", event.target.value);
  };

  const hanldeOTCalculationBasedOn = (event) => {
    const value = event.target.value;
    setValue("calculation_based_on", value);
    setValue("is_ot_payment_based_on", "Attendance");

    if (value === "Shift") {
      resetOtFixedAmount();
      resetOtAttendance();
    } else {
      resetOtSftTable();
    }
  };

  const handleOTPay = (event) => {
    setValue("is_ot_pay_by_default", event.target.checked);
  };

  const handleOTPaymentBasedOn = (event) => {
    const value = event.target.value;
    setValue("is_ot_payment_based_on", value);
    if (value === "Attendance") {
      resetOtFixedAmount();
    } else {
      resetOtAttendance();
    }
  };

  const handleWeekoffPaymentBasedOn = (event) => {
    setValue("is_weekoff_payment_based_on", event.target.value);
    if (watch("is_weekoff_payment_based_on") === "Attendance") {
      resetField("weekoff_attendance_fixed_amount");
      setValue("weekoff_attendance_fixed_amount", 0);
    } else {
      resetField("weekoff_attendance_payment_factor");
      setValue("weekoff_attendance_payment_factor", 0);
    }
  };

  const handleHolidayPaymentBasedOn = (event) => {
    setValue("is_holiday_payment_based_on", event.target.value);
    if (watch("is_holiday_payment_based_on") === "Attendance") {
      resetField("holiday_attendance_fixed_amount");
      setValue("holiday_attendance_fixed_amount", 0);
    } else {
      resetField("holiday_attendance_payment_factor");
      setValue("holiday_attendance_payment_factor", 0);
    }
  };

  const handleAbsencePaymentBasedOn = (event) => {
    setValue("absent_payment_based_on", event.target.value);
    setValue("partial_payment_factor", 1);
  };

  const onSubmit = async (formValues) => {
    const isValid =
      watch("is_overtime_allowed") && watch("calculation_based_on") === "Shift"
        ? validateTableRef.current()
        : true;
    if (isValid) {
      onUpdate({
        ...formValues,
        rows,
      });
      handleNext(2);
    }
  };

  useEffect(() => {
    if (data && Object.keys(data)?.length > 0) {
      reset(data);
      const mappedRows = data?.rows?.map((item) => ({
        work_min_percentage: item.work_min_percentage,
        count_as_shift: item.count_as_shift,
      }));
      setRows(mappedRows);
    }
  }, [data, reset]);

  useEffect(() => {
    if (clearAbsenceValues) {
      setValue("is_absence_paid", false);
      setValue("absent_payment_based_on", "Paid");
      setClearAbsenceValues(false);
    }
  });

  const resetHolidayPaymentFields = () => {
    const fields = [
      "holiday_attendance_payment_factor",
      "holiday_attendance_fixed_amount",
    ];
    fields.forEach((field) => {
      resetField(field, { keepError: false });
      setValue(field, 0, { shouldValidate: false });
    });
    setValue("is_holiday_payment_based_on", "Attendance");
  };

  const resetWeekOffFields = () => {
    const fields = [
      "weekoff_attendance_payment_factor",
      "weekoff_attendance_fixed_amount",
    ];

    fields.forEach((field) => {
      resetField(field, { keepError: false });
      setValue(field, 0, { shouldValidate: false });
    });
    setValue("is_weekoff_payment_based_on", "Attendance");
  };

  const resetOtFixedAmount = () => {
    const fields = [
      "ot_fixed_amount_thereshold_limit",
      "ot_fixed_amount_payable",
      "ot_fixed_amount_additional_mins",
      "ot_fixed_amount_additional_payable",
    ];

    fields.forEach((field) => {
      resetField(field, { keepError: false });
      setValue(field, 0, { shouldValidate: false });
    });
    setValue("fixed_amount_deduct_type", "");
  };

  const resetOtAttendance = () => {
    const fields = [
      "minimum_ot_consideration",
      "minutes_for_consideration",
      "additional_minutes_for_consideration",
      "additional_ot",
    ];

    fields.forEach((field) => {
      resetField(field, { keepError: false });
      setValue(field, 0, { shouldValidate: false });
    });
    setValue("attendance_deduct_type", "");
  };

  const resetOtSftTable = () => {
    const fields = [
      "deduct_additional_percentage",
      "deduct_shift_count",
      "ot_sft_payment_factor",
    ];
    fields.forEach((field) => {
      resetField(field, { keepError: false });
      setValue(field, 0, { shouldValidate: false });
    });
    setRows([{ work_min_percentage: "", count_as_shift: "" }]);
    setRowError([{ work_min_percentage: false, count_as_shift: false }]);
  };

  const resetOtData = () => {
    setValue("ot_work_preauthorization", "Automatic");
    setValue("ot_timing_privilege", "Only Before");
    setValue("calculation_based_on", "Hours");
    setValue("is_ot_payment_based_on", "Attendance");
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{ flexGrow: 1, overflow: "auto", p: 2 }}
        component="form"
        onSubmit={handleSubmit(onSubmit)}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
          }}
        >
          <Box sx={mainOptions}>
            <Box sx={boxStyle(watch("is_overtime_allowed"))}>
              <Typography fontWeight="medium">Is OverTime Allowed</Typography>

              <Switch
                checked={watch("is_overtime_allowed")}
                onChange={(event) => {
                  const checked = event.target.checked;
                  setValue("is_overtime_allowed", checked);
                  if (!checked) {
                    resetOtData();
                    resetOtSftTable();
                    resetOtAttendance();
                    resetOtFixedAmount();
                  }
                }}
              />
            </Box>
            {watch("is_overtime_allowed") && (
              <>
                <Box sx={{ ml: 2, mb: 2 }}>
                  <Typography fontWeight="medium">
                    OT work Preauthorization
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Define whether overtime is auto-approved or requires manual
                    approval.
                  </Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <RadioGroup
                    row
                    value={watch("ot_work_preauthorization")}
                    onChange={handleOTPreauthorization}
                    sx={optionsStyle}
                  >
                    {otPreauthorizationOptions.map((opt) => {
                      const isActive =
                        watch("ot_work_preauthorization") === opt.id;
                      return (
                        <Box key={opt.id} sx={cardStyle(isActive)}>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Radio value={opt.id} />
                            <Typography
                              fontWeight="medium"
                              sx={{
                                color: isActive
                                  ? themes.stepActiveColor
                                  : "inherit",
                              }}
                            >
                              {opt.label}
                            </Typography>
                          </Box>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              ml: 4,
                              maxWidth: 250,
                              wordBreak: "break-word",
                            }}
                          >
                            {opt.description}
                          </Typography>
                        </Box>
                      );
                    })}
                  </RadioGroup>
                </Box>
                {watch("ot_work_preauthorization") === "Automatic" && (
                  <>
                    <Box sx={{ ml: 2, mb: 2 }}>
                      <Typography fontWeight="medium">
                        OT timing privilege
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Specify when overtime hours count toward payment
                        calculation.
                      </Typography>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <RadioGroup
                        row
                        value={watch("ot_timing_privilege")}
                        onChange={hanldeOTPrivilege}
                        sx={optionsStyle}
                      >
                        {otTimingOptions.map((opt) => {
                          const isActive =
                            watch("ot_timing_privilege") === opt.id;
                          return (
                            <Box key={opt.id} sx={cardStyle(isActive)}>
                              <Box
                                sx={{ display: "flex", alignItems: "center" }}
                              >
                                <Radio value={opt.id} />
                                <Typography
                                  fontWeight="medium"
                                  sx={{
                                    color: isActive
                                      ? themes.stepActiveColor
                                      : "inherit",
                                  }}
                                >
                                  {opt.label}
                                </Typography>
                              </Box>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  ml: 4,
                                  maxWidth: 250,
                                  wordBreak: "break-word",
                                }}
                              >
                                {opt.description}
                              </Typography>
                            </Box>
                          );
                        })}
                      </RadioGroup>
                    </Box>
                  </>
                )}
                <Box sx={{ ml: 2, mb: 2 }}>
                  <Typography fontWeight="medium">
                    Calculation based on
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Choose whether overtime is calculated based on hours worked
                    or entire shifts.
                  </Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <RadioGroup
                    row
                    value={watch("calculation_based_on")}
                    onChange={hanldeOTCalculationBasedOn}
                    sx={optionsStyle}
                  >
                    {calculationOptions.map((opt) => {
                      const isActive = watch("calculation_based_on") === opt.id;
                      return (
                        <Box key={opt.id} sx={cardStyle(isActive)}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <Radio value={opt.id} />
                            <Typography
                              fontWeight="medium"
                              sx={{
                                color: isActive
                                  ? themes.stepActiveColor
                                  : "inherit",
                              }}
                            >
                              {opt.label}
                            </Typography>
                          </Box>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              ml: 4,
                              maxWidth: 250,
                              wordBreak: "break-word",
                            }}
                          >
                            {opt.description}
                          </Typography>
                          {opt.id === "Shift" &&
                            watch("calculation_based_on") === "Shift" && (
                              <Box sx={{ ...amtFactorStyle, mt: 0.5 }}>
                                <Typography fontWeight="medium">
                                  Payment Factor{" "}
                                  <span style={{ color: themes?.redColor }}>
                                    *
                                  </span>
                                </Typography>
                                <CustomBox
                                  name="ot_sft_payment_factor"
                                  type="shift"
                                  value={watch("ot_sft_payment_factor")}
                                  register={register}
                                  errors={errors}
                                  handleChange={(e, value) => {
                                    setValue("ot_sft_payment_factor", value);
                                  }}
                                  rules={{ required: "Enter value" }}
                                  inputWidth="50px"
                                />
                              </Box>
                            )}
                        </Box>
                      );
                    })}
                  </RadioGroup>
                </Box>
                {watch("calculation_based_on") === "Shift" && (
                  <>
                    <Box sx={{ px: 2, py: 1, mb: 2 }}>
                      <ShiftWorkTable
                        rows={rows}
                        setRows={setRows}
                        rowError={rowError}
                        setRowError={setRowError}
                        onValidateRef={validateTableRef}
                      />
                    </Box>
                    <Box sx={additionalInfoStyle}>
                      <Box sx={individualBoxStyle}>
                        <Typography variant="body2" color="text.secondary">
                          Each additional
                        </Typography>
                        <CustomBox
                          name="deduct_additional_percentage"
                          type="percentage"
                          value={watch("deduct_additional_percentage")}
                          register={register}
                          errors={errors}
                          handleChange={(e, value) => {
                            setValue("deduct_additional_percentage", value);
                          }}
                          rules={{ required: "Enter value" }}
                          inputWidth="50px"
                        />

                        <Typography variant="body2" color="text.secondary">
                          percentage is counted as
                        </Typography>
                        <CustomBox
                          name="deduct_shift_count"
                          type="shift"
                          value={watch("deduct_shift_count")}
                          register={register}
                          errors={errors}
                          handleChange={(e, value) => {
                            setValue("deduct_shift_count", value);
                          }}
                          rules={{ required: "Enter value" }}
                          inputWidth="50px"
                        />

                        <Typography variant="body2" color="text.secondary">
                          shift
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={infoMessageStyle}>
                      <ShiftWorkExample />
                    </Box>
                  </>
                )}

                {watch("calculation_based_on") === "Hours" && (
                  <>
                    <Box sx={{ ml: 2, mb: 2 }}>
                      <Typography fontWeight="medium">
                        Payment Based On
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Decide whether payment is linked to attendance factor or
                        a fixed amount
                      </Typography>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <RadioGroup
                        row
                        value={watch("is_ot_payment_based_on")}
                        onChange={handleOTPaymentBasedOn}
                        sx={optionsStyle}
                      >
                        {otPaymentOptions.map((opt) => {
                          const isActive =
                            watch("is_ot_payment_based_on") === opt.id;
                          return (
                            <Box key={opt.id} sx={cardStyle(isActive)}>
                              <Box
                                sx={{ display: "flex", alignItems: "center" }}
                              >
                                <Radio value={opt.id} />
                                <Typography
                                  fontWeight="medium"
                                  sx={{
                                    color: isActive
                                      ? themes.stepActiveColor
                                      : "inherit",
                                  }}
                                >
                                  {opt.label}
                                </Typography>
                              </Box>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  ml: 4,
                                  maxWidth: 250,
                                  wordBreak: "break-word",
                                }}
                              >
                                {opt.description}
                              </Typography>
                            </Box>
                          );
                        })}
                      </RadioGroup>
                    </Box>
                    {watch("is_ot_payment_based_on") === "Attendance" ? (
                      <>
                        <Box sx={additionalInfoStyle}>
                          <Box sx={individualBoxStyle}>
                            <Typography variant="body2" color="text.secondary">
                              The threshold limit for overtime is
                            </Typography>
                            <CustomBox
                              name="minimum_ot_consideration"
                              type="minutes"
                              value={watch("minimum_ot_consideration")}
                              register={register}
                              errors={errors}
                              handleChange={(e, value) => {
                                setValue("minimum_ot_consideration", value);
                              }}
                              rules={{ required: "Enter value" }}
                              inputWidth="50px"
                            />
                            <Typography variant="body2" color="text.secondary">
                              mins will be considered as
                            </Typography>
                            <CustomBox
                              name="minutes_for_consideration"
                              type="minutes"
                              value={watch("minutes_for_consideration")}
                              register={register}
                              errors={errors}
                              handleChange={(e, value) => {
                                setValue("minutes_for_consideration", value);
                              }}
                              rules={{ required: "Enter value" }}
                              inputWidth="50px"
                            />
                            <Typography variant="body2" color="text.secondary">
                              mins
                            </Typography>
                            <Box
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                width: 180,
                                verticalAlign: "middle",
                              }}
                            >
                              <CustomAutoComplete
                                dataList={sftType
                                  ?.filter((obj) => obj.label !== "Per Cycle")
                                  .map((obj) => ({
                                    ...obj,
                                    label: obj.label,
                                  }))}
                                rules={{
                                  ...register("attendance_deduct_type", {
                                    required: false,
                                    validate: {
                                      minLengthNoSpaces: (value) => {
                                        if (
                                          watch("is_overtime_allowed") &&
                                          watch("is_ot_payment_based_on") ===
                                            "Attendance" &&
                                          watch("calculation_based_on") ===
                                            "Hours" &&
                                          watch("attendance_deduct_type") === ""
                                        ) {
                                          return "Value is required";
                                        } else {
                                          return true;
                                        }
                                      },
                                    },
                                  }),
                                }}
                                selectedValue={watch("attendance_deduct_type")}
                                updateDataFunction={(event, data) =>
                                  setValue(
                                    "attendance_deduct_type",
                                    data?.label
                                  )
                                }
                                placeHolder="Select type"
                                errorMessage={
                                  errors?.attendance_deduct_type?.message
                                    ? errors?.attendance_deduct_type?.message
                                    : ""
                                }
                                textBoxError={
                                  errors?.attendance_deduct_type ? true : false
                                }
                              />
                            </Box>
                          </Box>
                        </Box>
                        <Box sx={additionalInfoStyle}>
                          <Box sx={individualBoxStyle}>
                            <Typography variant="body2" color="text.secondary">
                              After that, every additional
                            </Typography>
                            <CustomBox
                              name="additional_ot"
                              type="minutes"
                              value={watch("additional_ot")}
                              register={register}
                              errors={errors}
                              handleChange={(e, value) => {
                                setValue("additional_ot", value);
                              }}
                              rules={{ required: "Enter value" }}
                              inputWidth="50px"
                            />
                            <Typography variant="body2" color="text.secondary">
                              mins will be considered as
                            </Typography>
                            <CustomBox
                              name="additional_minutes_for_consideration"
                              type="minutes"
                              value={watch(
                                "additional_minutes_for_consideration"
                              )}
                              register={register}
                              errors={errors}
                              handleChange={(e, value) => {
                                setValue(
                                  "additional_minutes_for_consideration",
                                  value
                                );
                              }}
                              rules={{ required: "Enter value" }}
                              inputWidth="50px"
                            />
                            <Typography variant="body2" color="text.secondary">
                              mins OT {watch("attendance_deduct_type")}
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={infoMessageStyle}>
                          <PaymentBasedAttendanceExample />
                        </Box>
                      </>
                    ) : (
                      <>
                        <Box sx={additionalInfoStyle}>
                          <Box sx={individualBoxStyle}>
                            <Typography variant="body2" color="text.secondary">
                              The threshold limit for ovetime is
                            </Typography>
                            <CustomBox
                              name="ot_fixed_amount_thereshold_limit"
                              type="minutes"
                              value={watch("ot_fixed_amount_thereshold_limit")}
                              register={register}
                              errors={errors}
                              handleChange={(e, value) => {
                                setValue(
                                  "ot_fixed_amount_thereshold_limit",
                                  value
                                );
                              }}
                              rules={{ required: "Enter value" }}
                              inputWidth="50px"
                            />

                            <Typography variant="body2" color="text.secondary">
                              mins with an amount payable of
                            </Typography>
                            <CustomBox
                              name="ot_fixed_amount_payable"
                              type="amount"
                              value={watch("ot_fixed_amount_payable")}
                              register={register}
                              errors={errors}
                              handleChange={(e, value) => {
                                setValue("ot_fixed_amount_payable", value);
                              }}
                              rules={{ required: "Enter value" }}
                              inputWidth="50px"
                            />

                            <Box
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                width: 180,
                                verticalAlign: "middle",
                              }}
                            >
                              <CustomAutoComplete
                                dataList={sftType?.map((obj) => ({
                                  ...obj,
                                  label: obj?.label,
                                }))}
                                rules={{
                                  ...register("fixed_amount_deduct_type", {
                                    required: false,
                                    validate: {
                                      minLengthNoSpaces: (value) => {
                                        if (
                                          watch("is_overtime_allowed") &&
                                          watch("is_ot_payment_based_on") ===
                                            "Amount" &&
                                          watch("calculation_based_on") ===
                                            "Hours" &&
                                          watch("fixed_amount_deduct_type") ===
                                            ""
                                        ) {
                                          return "Value is required";
                                        } else {
                                          return true;
                                        }
                                      },
                                    },
                                  }),
                                }}
                                selectedValue={watch(
                                  "fixed_amount_deduct_type"
                                )}
                                updateDataFunction={(event, data) =>
                                  setValue(
                                    "fixed_amount_deduct_type",
                                    data?.label
                                  )
                                }
                                placeHolder="Select type"
                                errorMessage={
                                  errors?.fixed_amount_deduct_type?.message
                                    ? errors?.fixed_amount_deduct_type?.message
                                    : ""
                                }
                                textBoxError={
                                  errors?.fixed_amount_deduct_type
                                    ? true
                                    : false
                                }
                              />
                            </Box>
                          </Box>
                        </Box>
                        <Box sx={additionalInfoStyle}>
                          <Box sx={individualBoxStyle}>
                            <Typography variant="body2" color="text.secondary">
                              For every additional
                            </Typography>
                            <CustomBox
                              name="ot_fixed_amount_additional_mins"
                              type="minutes"
                              value={watch("ot_fixed_amount_additional_mins")}
                              register={register}
                              errors={errors}
                              handleChange={(e, value) => {
                                setValue(
                                  "ot_fixed_amount_additional_mins",
                                  value
                                );
                              }}
                              rules={{ required: "Enter value" }}
                              inputWidth="50px"
                            />

                            <Typography variant="body2" color="text.secondary">
                              mins an additional
                            </Typography>
                            <CustomBox
                              name="ot_fixed_amount_additional_payable"
                              type="amount"
                              value={watch(
                                "ot_fixed_amount_additional_payable"
                              )}
                              register={register}
                              errors={errors}
                              handleChange={(e, value) => {
                                setValue(
                                  "ot_fixed_amount_additional_payable",
                                  value
                                );
                              }}
                              rules={{ required: "Enter value" }}
                              inputWidth="50px"
                            />

                            <Typography variant="body2" color="text.secondary">
                              will be payable{" "}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {watch("fixed_amount_deduct_type")}
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={infoMessageStyle}>
                          <PaymentBasedFixedAmountExample />
                        </Box>
                      </>
                    )}
                  </>
                )}

                <Box
                  sx={{
                    ml: 2,
                    mr: 2,
                  }}
                >
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ mb: 2 }}>
                      <Typography fontWeight="medium">
                        {" "}
                        Is OT pay added to payroll by default
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Enable to auto-add approved OT to relevent
                        payroll.Disable to Keep OT in backlog for manual
                        addition later.
                      </Typography>
                    </Box>
                    <Switch
                      checked={watch("is_ot_pay_by_default")}
                      onChange={handleOTPay}
                    />
                  </Box>
                </Box>
              </>
            )}
          </Box>

          <Box sx={mainOptions}>
            <Box sx={boxStyle}>
              <Typography fontWeight="medium">Weekoff</Typography>
            </Box>

            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              sx={{ ml: 2, mb: 2 }}
            >
              <Box>
                <Typography fontWeight="medium">Is Weekoff Paid</Typography>
              </Box>
              <Box sx={{ mr: 1.6 }}>
                <Switch
                  checked={watch("is_weekoff_paid")}
                  onChange={(event) => {
                    const checked = event.target.checked;
                    setValue("is_weekoff_paid", checked);
                  }}
                />
              </Box>
            </Box>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              sx={{ ml: 2, mb: 2 }}
            >
              <Box>
                <Typography fontWeight="medium">
                  Payment for weekoff day attendance
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Pay employees who work on a weekoff.
                </Typography>
              </Box>
              <Box sx={{ mr: 1.6 }}>
                <Switch
                  checked={watch("is_payment_for_weekoff_day_attendance")}
                  onChange={(event) => {
                    const checked = event.target.checked;
                    setValue(
                      "is_payment_for_weekoff_day_attendance",
                      event.target.checked
                    );
                    if (!checked) resetWeekOffFields();
                  }}
                />
              </Box>
            </Box>
            {watch("is_payment_for_weekoff_day_attendance") && (
              <>
                <Box sx={{ ml: 2, mb: 2 }}>
                  <Box>
                    <Typography fontWeight="medium">
                      Payment based on :
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Decide whether payment is linked to attendance factor or a
                      fixed amount
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <RadioGroup
                    row
                    value={watch("is_weekoff_payment_based_on")}
                    onChange={handleWeekoffPaymentBasedOn}
                    sx={optionsStyle}
                  >
                    {weekoffPaymentOptions.map((opt) => {
                      const isActive =
                        watch("is_weekoff_payment_based_on") === opt.id;
                      return (
                        <Box key={opt.id} sx={cardStyle(isActive)}>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Radio value={opt.id} />
                            <Typography
                              fontWeight="medium"
                              sx={{
                                color: isActive
                                  ? themes.stepActiveColor
                                  : "inherit",
                              }}
                            >
                              {opt.label}
                            </Typography>
                          </Box>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              ml: 4,
                              maxWidth: 250,
                              wordBreak: "break-word",
                            }}
                          >
                            {opt.description}
                          </Typography>
                          {opt.id === "Attendance" &&
                            watch("is_weekoff_payment_based_on") ===
                              "Attendance" && (
                              <Box sx={amtFactorStyle}>
                                <Typography fontWeight="medium">
                                  Payment Factor{" "}
                                  <span style={{ color: themes?.redColor }}>
                                    *
                                  </span>
                                </Typography>
                                <CustomBox
                                  name="weekoff_attendance_payment_factor"
                                  type="shift"
                                  value={watch(
                                    "weekoff_attendance_payment_factor"
                                  )}
                                  register={register}
                                  errors={errors}
                                  handleChange={(e, value) => {
                                    setValue(
                                      "weekoff_attendance_payment_factor",
                                      value
                                    );
                                  }}
                                  rules={{ required: "Enter value" }}
                                  inputWidth="50px"
                                />
                              </Box>
                            )}

                          {opt.id === "Amount" &&
                            watch("is_weekoff_payment_based_on") ===
                              "Amount" && (
                              <Box sx={amtFactorStyle}>
                                <Typography fontWeight="medium">
                                  Fixed Amount{" "}
                                  <span style={{ color: themes?.redColor }}>
                                    *
                                  </span>
                                </Typography>
                                <CustomBox
                                  name="weekoff_attendance_fixed_amount"
                                  type="amount"
                                  value={watch(
                                    "weekoff_attendance_fixed_amount"
                                  )}
                                  register={register}
                                  errors={errors}
                                  handleChange={(e, value) => {
                                    setValue(
                                      "weekoff_attendance_fixed_amount",
                                      value
                                    );
                                  }}
                                  rules={{ required: "Enter value" }}
                                  inputWidth="50px"
                                />
                              </Box>
                            )}
                        </Box>
                      );
                    })}
                  </RadioGroup>
                </Box>
                <Box sx={infoMessageStyle}>
                  <PaidWeekOffExample />
                </Box>
              </>
            )}
          </Box>

          <Box sx={mainOptions}>
            <Box sx={boxStyle}>
              <Typography fontWeight="medium">Holiday</Typography>
            </Box>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              sx={{ ml: 2, mb: 2 }}
            >
              <Box>
                <Typography fontWeight="medium">Is Holiday Paid</Typography>
              </Box>
              <Box sx={{ mr: 1.6 }}>
                <Switch
                  checked={watch("is_holiday_paid")}
                  onChange={(event) => {
                    const checked = event.target.checked;
                    setValue("is_holiday_paid", checked);
                  }}
                />
              </Box>
            </Box>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              sx={{ ml: 2, mb: 2 }}
            >
              <Box>
                <Typography fontWeight="medium">
                  Payment for holiday day attendance
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Pay employees who work on a holidays.
                </Typography>
              </Box>
              <Box sx={{ mr: 1.6 }}>
                <Switch
                  {...register("is_payment_for_holiday_day_attendance")}
                  checked={watch("is_payment_for_holiday_day_attendance")}
                  onChange={(event) => {
                    const checked = event.target.checked;
                    setValue("is_payment_for_holiday_day_attendance", checked);
                    if (!checked) resetHolidayPaymentFields();
                  }}
                />
              </Box>
            </Box>
            {watch("is_payment_for_holiday_day_attendance") && (
              <>
                <Box sx={{ ml: 2, mb: 2 }}>
                  <Box>
                    <Typography fontWeight="medium">
                      Payment for holiday attendance based on :
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Choose between the payment factor (linked to attendance)
                      or fixed amount
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <RadioGroup
                    row
                    value={watch("is_holiday_payment_based_on")}
                    onChange={handleHolidayPaymentBasedOn}
                    sx={optionsStyle}
                  >
                    {holidayPaymentOptions.map((opt) => {
                      const isActive =
                        watch("is_holiday_payment_based_on") === opt.id;
                      return (
                        <Box key={opt.id} sx={cardStyle(isActive)}>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Radio value={opt.id} />
                            <Typography
                              fontWeight="medium"
                              sx={{
                                color: isActive
                                  ? themes.stepActiveColor
                                  : "inherit",
                              }}
                            >
                              {opt.label}
                            </Typography>
                          </Box>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              ml: 4,
                              maxWidth: 250,
                              wordBreak: "break-word",
                            }}
                          >
                            {opt.description}
                          </Typography>
                          {opt.id === "Attendance" &&
                            watch("is_holiday_payment_based_on") ===
                              "Attendance" && (
                              <Box sx={amtFactorStyle}>
                                <Typography fontWeight="medium">
                                  Payment Factor{" "}
                                  <span style={{ color: themes?.redColor }}>
                                    *
                                  </span>
                                </Typography>
                                <CustomBox
                                  name="holiday_attendance_payment_factor"
                                  type="shift"
                                  value={watch(
                                    "holiday_attendance_payment_factor"
                                  )}
                                  register={register}
                                  errors={errors}
                                  handleChange={(e, value) => {
                                    setValue(
                                      "holiday_attendance_payment_factor",
                                      value
                                    );
                                  }}
                                  rules={{ required: "Enter value" }}
                                  inputWidth="50px"
                                />
                              </Box>
                            )}

                          {opt.id === "Amount" &&
                            watch("is_holiday_payment_based_on") ===
                              "Amount" && (
                              <Box sx={amtFactorStyle}>
                                <Typography fontWeight="medium">
                                  Fixed Amount{" "}
                                  <span style={{ color: themes?.redColor }}>
                                    *
                                  </span>
                                </Typography>
                                <CustomBox
                                  name="holiday_attendance_fixed_amount"
                                  type="amount"
                                  value={watch(
                                    "holiday_attendance_fixed_amount"
                                  )}
                                  register={register}
                                  errors={errors}
                                  handleChange={(e, value) => {
                                    setValue(
                                      "holiday_attendance_fixed_amount",
                                      value
                                    );
                                  }}
                                  rules={{ required: "Enter value" }}
                                  inputWidth="50px"
                                />
                              </Box>
                            )}
                        </Box>
                      );
                    })}
                  </RadioGroup>
                </Box>
                <Box sx={infoMessageStyle}>
                  <PaidHolidayExample />
                </Box>
              </>
            )}
          </Box>

          <Box sx={mainOptions}>
            <Box sx={boxStyle(watch("is_absence_paid"))}>
              <Typography fontWeight="medium">Is Absence Paid</Typography>
              <Switch
                checked={watch("is_absence_paid")}
                onChange={(event) => {
                  const checked = event.target.checked;
                  setValue("is_absence_paid", event.target.checked);
                  if (!checked) {
                    setValue("absent_payment_based_on", "Paid");
                    resetField("partial_payment_factor");
                    setValue("partial_payment_factor", 0);
                  }
                }}
              />
            </Box>
            {watch("is_absence_paid") && (
              <>
                <Box sx={{ mb: 2 }}>
                  <RadioGroup
                    row
                    value={watch("absent_payment_based_on")}
                    onChange={handleAbsencePaymentBasedOn}
                    sx={optionsStyle}
                  >
                    {isAbsenceOptions.map((opt) => {
                      const isActive =
                        watch("absent_payment_based_on") === opt.id;
                      return (
                        <Box key={opt.id} sx={cardStyle(isActive)}>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Radio value={opt.id} />
                            <Typography
                              fontWeight="medium"
                              sx={{
                                color: isActive
                                  ? themes.stepActiveColor
                                  : "inherit",
                              }}
                            >
                              {opt.label}
                            </Typography>
                          </Box>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              ml: 4,
                              maxWidth: 250,
                              wordBreak: "break-word",
                            }}
                          >
                            {opt.description}
                          </Typography>
                          {opt.id === "Partially Paid" &&
                            watch("absent_payment_based_on") ===
                              "Partially Paid" && (
                              <Box sx={amtFactorStyle}>
                                <Typography fontWeight="medium">
                                  Payment Factor{" "}
                                  <span style={{ color: themes?.redColor }}>
                                    *
                                  </span>
                                </Typography>
                                <CustomBox
                                  name="partial_payment_factor"
                                  type="shift"
                                  value={watch("partial_payment_factor")}
                                  register={register}
                                  errors={errors}
                                  handleChange={(e, value) => {
                                    setValue("partial_payment_factor", value);
                                  }}
                                  rules={{ required: "Enter value" }}
                                  inputWidth="50px"
                                />
                              </Box>
                            )}
                        </Box>
                      );
                    })}
                  </RadioGroup>
                </Box>
                {watch("absent_payment_based_on") === "Paid" ? (
                  <Box sx={infoMessageStyle}>
                    <IsAbsencePaidExample />
                  </Box>
                ) : (
                  <Box sx={infoMessageStyle}>
                    <IsAbsenceExample />
                  </Box>
                )}
              </>
            )}
          </Box>

          <Box
            sx={{
              ml: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <CustomButton
                actionName="Prev"
                typeName="button"
                actionFuntion={() => {
                  handleBack(0);
                }}
              />
            </Box>
            <Box>
              <CustomButton actionName="Next" typeName="submit" />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
